/**
 * LeadApprovalsPage — Lead Manager's view of their submitted reassignment
 * approval requests, with status badges.
 *
 * Apr 26, 2026 (Squad-B). Stub UI; backend `LeadApprovalRequest` may not be
 * online yet — degrades to empty state gracefully.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';

interface ApprovalRequestRow {
  id: string;
  leadId?: string | null;
  leadName?: string | null;
  fromUserName?: string | null;
  toUserName?: string | null;
  reason?: string | null;
  status?: string | null;
  createdAt?: string | null;
  decidedAt?: string | null;
}

function StatusChip({ status }: { status: string | null | undefined }) {
  const s = (status ?? 'pending').toLowerCase();
  const map: Record<string, { bg: string; fg: string }> = {
    pending: { bg: 'var(--ivory-3)', fg: 'var(--ink-2)' },
    approved: { bg: 'var(--forest-100)', fg: 'var(--forest-600)' },
    rejected: { bg: 'rgba(207, 80, 80, 0.12)', fg: 'var(--danger)' },
  };
  const tone = map[s] ?? map['pending'];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: 10,
        fontWeight: 500,
        borderRadius: 4,
        textTransform: 'capitalize',
        color: tone.fg,
        background: tone.bg,
      }}
    >
      {s}
    </span>
  );
}

export function LeadApprovalsPage() {
  const [rows, setRows] = useState<ApprovalRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<{ items?: ApprovalRequestRow[]; data?: ApprovalRequestRow[] } | ApprovalRequestRow[]>(
      '/lead-approval-requests?scope=mine&status=pending',
    ).then((r) => {
      const raw = r.data;
      const list: ApprovalRequestRow[] = Array.isArray(raw)
        ? raw
        : (raw as { items?: ApprovalRequestRow[]; data?: ApprovalRequestRow[] })?.items
        ?? (raw as { items?: ApprovalRequestRow[]; data?: ApprovalRequestRow[] })?.data
        ?? [];
      setRows(list);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link
          to="/dashboard"
          style={{
            color: 'var(--ink-3)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
          }}
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      <div style={{ marginBottom: 18 }}>
        <h2
          style={{
            fontFamily: 'var(--ff-serif)',
            fontSize: 22,
            fontWeight: 500,
            marginBottom: 2,
            color: 'var(--ink-0)',
          }}
        >
          My reassignment requests
        </h2>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Approvals you have raised — pending or recently decided.
        </div>
      </div>

      <div
        style={{
          background: 'var(--ivory-1)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 28, fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
            Loading…
          </div>
        ) : error || rows.length === 0 ? (
          <div style={{ padding: 36, textAlign: 'center' }}>
            <ShieldCheck size={28} style={{ color: 'var(--ink-3)', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, marginBottom: 4 }}>
              {error ? 'Reassignment approval queue coming soon' : 'No reassignment requests yet'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 360, margin: '0 auto' }}>
              {error
                ? 'Backend `LeadApprovalRequest` schema is being shipped by Squad-A. Once live, your raised approvals will appear here.'
                : 'When you request a lead reassignment, it lands here with the admin’s decision.'}
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ivory-2)', borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Lead
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  From → To
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Reason
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((req) => (
                <tr key={req.id} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-0)', fontWeight: 500 }}>
                    {req.leadName ?? req.leadId ?? '—'}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {req.fromUserName ?? '—'} → {req.toUserName ?? '—'}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-2)' }}>
                    {req.reason ?? '—'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <StatusChip status={req.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default LeadApprovalsPage;
